/**
 * @Page RST02
 * @Description [RST02] 완성차 재고관리 > 기준정보 > 상주원 등록
 *
 * [좌측] 상주원 목록 조회/등록/삭제 (WRTRESI 상주원 마스터)
 * [우측] 선택한 상주원의 품목그룹코드 배정 목록 (WRTRSGP 매핑 데이터)
 *        - 신규 버튼: ResiGrp 팝업 → /rst03/wagr/search 체크박스 선택 → INSERT
 *        - 삭제 버튼: 선택 행 → /rst02/resi/grpdelete
 **/
import React, { useState, useEffect, useRef, useCallback } from "react";
import Ribbon from '@/components/organisms/Ribbon';
import GridRibbon from "@/components/organisms/GridRibbon";
import ContentXearch from '@/components/organisms/ContentXearch';
import Content from '@/components/organisms/Content';
import Grid from '@/components/grid/agGrid';
import CustomAlert from "@/components/molecules/CustomAlert";
import AlertDialog from '@/components/material-UI/AlertDialog';
import Popup from '@/pages/Popup/Popup';
import * as Common from '@/utils/Common';
import CommonUtil from '@/utils/CommonUtil';


const SERVER_URL = process.env.REACT_APP_BACKEND_SERVER;
const Utils = new CommonUtil();

const useYnList = [
    { comCode: "*", text: "전체" },
    { comCode: "Y", text: "사용" },
    { comCode: "N", text: "미사용" },
];

const RESIDENT_NAME_MAX_BYTES = 15;

const truncateByUtf8Bytes = (value, maxBytes) => {
    const text = String(value ?? "");
    let result = "";
    let bytes = 0;

    for (const character of text) {
        const characterBytes = new TextEncoder().encode(character).length;
        if (bytes + characterBytes > maxBytes) break;
        result += character;
        bytes += characterBytes;
    }

    return result;
};

const formatPhone = value => {
    const digits = String(value ?? "").replace(/\D/g, "");
    if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, "$1-$2-$3");
    if (digits.length === 10) return digits.startsWith("02")
        ? digits.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3")
        : digits.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    if (digits.length === 9 && digits.startsWith("02")) return digits.replace(/(\d{2})(\d{3})(\d{4})/, "$1-$2-$3");
    return value ?? "";
};

function RST02() {
    const token = Common.getToken();
    const userId = Common.getToken("id");
    const tVnt1 = Common.getToken("vand");
    const tComp = Common.getToken("comp");

    const grdRST02List = useRef();  // 좌측: 상주원 마스터
    const grdRST02Detail = useRef();  // 우측: 품목그룹 배정 목록

    // ─── 공통 상태 ────────────────────────────────────────────────────────
    const [message, setMessage] = React.useState({ openAlert: false, severity: "success", msg: "" });
    const { openAlert, severity, msg } = message;

    const [dlgMessage, setDlgMessage] = React.useState({ openDlg: false, dlgTrans: "", dlgMsg: "" });
    const { openDlg, dlgTrans, dlgMsg } = dlgMessage;

    // ─── 검색 조건 ────────────────────────────────────────────────────────
    const ckVnt1 = Common.getCookie("ck_vnt1" + userId) || tVnt1;

    const [idata, setData] = useState({
        kVnt1: ckVnt1,
        kComp: tComp,
        kRecd: "",
        kUseyn: "*",
        vnt1List: [],
    });

    // ─── 레이아웃 ──────────────────────────────────────────────────────────
    const [panded, setPanded] = React.useState(true);
    const [style, setStyle] = useState({ height: "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)" });

    // ─── 좌측 그리드 API ──────────────────────────────────────────────────
    let [searchApi, setSearchApi] = useState(false);
    let [insertApi, setInsertApi] = useState(false);
    let [updateApi, setUpdateApi] = useState(false);
    let [deleteApi, setDeleteApi] = useState(false);

    const onClearApi = useCallback(() => {
        setSearchApi(false);
        setInsertApi(false);
        setUpdateApi(false);
        setDeleteApi(false);
    }, []);

    // ─── 우측 그리드 API ──────────────────────────────────────────────────
    let [searchApi2, setSearchApi2] = useState(false);
    let [deleteApi2, setDeleteApi2] = useState(false);
    let [clearGrid2, setClearGrid2] = useState(false);

    const onClearApi2 = useCallback(() => {
        setSearchApi2(false);
        setDeleteApi2(false);
    }, []);

    // ─── 현재 선택된 상주원 ────────────────────────────────────────────────
    const [currentRecd, setCurrentRecd] = useState("");
    const [currentRenm, setCurrentRenm] = useState("");
    const [currentUseyn, setCurrentUseyn] = useState("");
    const [leftSelectedCount, setLeftSelectedCount] = useState(0);
    const [detailSelectedCount, setDetailSelectedCount] = useState(0);

    // ─── ResiGrp 팝업 ─────────────────────────────────────────────────────
    const [resiGrpOpen, setResiGrpOpen] = useState(false);
    const nextResidentCodeRef = useRef(null);

    // ─── 마운트 ────────────────────────────────────────────────────────────
    useEffect(() => {
        onSearchCommon(`{"pgmid":"VNT1"}`, "vnt1List");
        onSearchLeft();
    }, []);

    // ─── 공통코드 조회 ─────────────────────────────────────────────────────
    const onSearchCommon = (para, name) => {
        const fetchData = async () => {
            const param = encodeURI(para);
            const query = SERVER_URL + "/comcd/search?query=" + param;
            await fetch(query, { headers: { Authorization: token } })
                .then(r => r.json())
                .then(data => {
                    if (data.success) setData(prev => ({ ...prev, [name]: data.data }));
                });
        };
        fetchData();
    };

    // ─── onChange ──────────────────────────────────────────────────────────
    const onChange = (e) => {
        const { name, value } = e.target;
        setData(prev => ({ ...prev, [name]: value }));
    };

    // ─── 좌측 조회 ─────────────────────────────────────────────────────────
    const onSearchLeft = () => {
        nextResidentCodeRef.current = null;
        setLeftSelectedCount(0);
        const para = `{"kVnt1":"${idata.kVnt1}","recd":"${idata.kRecd}","useyn":"${idata.kUseyn}"}`;
        setSearchApi(SERVER_URL + "/rst02/resi/search?query=" + encodeURI(para));
    };

    const onSearch = () => {
        Common.setCookie("ck_vnt1" + userId, idata.kVnt1, 1);
        onSearchLeft();
        // 우측 초기화
        setCurrentRecd("");
        setCurrentRenm("");
        setCurrentUseyn("");
        setClearGrid2(true);
    };
    const onSearchKeyDown = e => { if (e.key === "Enter") { e.preventDefault(); onSearch(); } };

    // ─── 우측 조회 (선택 상주원 기준) ─────────────────────────────────────
    const onSearchRight = (recd) => {   
        setDetailSelectedCount(0);
        const para = `{"kVnt1":"${idata.kVnt1}","recd":"${recd}", "vand":"${idata.kVnt1}","_refresh":"${Date.now()}"}`;
        setSearchApi2(SERVER_URL + "/rst02/resi/grpsearch?query=" + encodeURI(para));
        // setSearchApi2(SERVER_URL + "/rst02/resi/grpsearch?query=" + encodeURI(para));
    };

    // ─── 좌측 신규 Row 추가 ────────────────────────────────────────────────
    const onAddRow = async () => {
        try {
            if (!nextResidentCodeRef.current) {
                const response = await fetch(SERVER_URL + "/rst02/resi/nextcode?vand=" + encodeURIComponent(idata.kVnt1), { headers: { Authorization: token } });
                const data = await response.json();
                nextResidentCodeRef.current = data.data || `${idata.kVnt1}001`;
            }
            const recd = nextResidentCodeRef.current;
            const seq = Number(String(recd).slice(String(idata.kVnt1).length)) || 0;
            nextResidentCodeRef.current = `${idata.kVnt1}${String(seq + 1).padStart(3, "0")}`;
            grdRST02List.current.addRow({ vand: idata.kVnt1, recd, renm: "", reph: "", useyn: "Y", gocd: "ADD" }, 0);
        } catch (e) {
            setMessage({ openAlert: true, severity: "error", msg: "상주원 코드 발번에 실패했습니다." });
        }
    };

    // ─── 좌측 입력 유효성 검사 ─────────────────────────────────────────────
    const inputCheck = (gubn) => {
        //수정일경우에는 상주원 코드는 수정할 수 없다 구분자 추가해야한다.
        //inputcheck에서 수정항목 체크 해야한다.
        let gubntrue = (gubn === "I" ? true : false); // 입력에 해당하는 예외처리 사항

        const selected = grdRST02List.current?.getSelectedNodes() || [];
        if (selected.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택된 데이터가 없습니다." });
            return false;
        }
        for (const node of selected) {
            const d = node.data;
            if (!d.recd && gubntrue) { // 입력조건
                setMessage({ openAlert: true, severity: "info", msg: "상주원코드를 입력하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "recd");
                return false;
            }
            if (d.recd && d.recd.substring(0, 4).toUpperCase() !== String(d.vand || idata.kVnt1).substring(0, 4).toUpperCase()) {
                setMessage({ openAlert: true, severity: "info", msg: "업체코드로 시작하는 상주원 코드만 등록할 수 있습니다." });
                grdRST02List.current.SetFocus(node.rowIndex, "recd");
                return false;
            }
            if (!d.renm) {
                setMessage({ openAlert: true, severity: "info", msg: "상주원명을 입력하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "renm");
                return false;
            }
            if (new TextEncoder().encode(String(d.renm)).length > RESIDENT_NAME_MAX_BYTES) {
                setMessage({
                    openAlert: true,
                    severity: "info",
                    msg: `상주원명은 ${RESIDENT_NAME_MAX_BYTES}바이트까지 입력할 수 있습니다.`,
                });
                grdRST02List.current.SetFocus(node.rowIndex, "renm");
                return false;
            }
            const phoneDigits = String(d.reph ?? "").replace(/\D/g, "");
            const validPhone = /^(02\d{7,8}|0[3-9]\d{8,9}|01[016789]\d{7,8})$/.test(phoneDigits);
            if (!validPhone) {
                setMessage({ openAlert: true, severity: "info", msg: "연락처를 올바른 형식으로 입력하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "reph");
                return false;
            }
            if (!d.useyn) {
                setMessage({ openAlert: true, severity: "info", msg: "사용여부를 선택하십시오." });
                grdRST02List.current.SetFocus(node.rowIndex, "useyn");
                return false;
            }
        }
        return true;
    };

    // ─── 좌측 입력 ─────────────────────────────────────────────────────────
    const onInsert = () => {
        if (!inputCheck("I")) return;
        setInsertApi(SERVER_URL + "/rst02/resi/insert");
    };

    const onUpdate = () => {
        if (!inputCheck("U")) return;
        setUpdateApi(SERVER_URL + "/rst02/resi/update");
    };

    // ─── 좌측 삭제 ─────────────────────────────────────────────────────────
    const onDelete = () => {
        const selected = grdRST02List.current?.getSelectedNodes() || [];
        if (selected.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택한 데이터가 없습니다." });
            return;
        }
        setDlgMessage({ openDlg: true, dlgTrans: "D_LEFT", dlgMsg: "선택한 상주원을 삭제하시겠습니까?" });
    };

    // ─── 좌측 Row 클릭 → 우측 그리드 조회 ────────────────────────────────
    const onRowClicked = (e) => {
        console.log("좌측 데이터 클릭e")
        console.log(e)
        if (!e.data) return;
        if (e.node && !e.node.isSelected()) {
            e.node.setSelected(true, false, "rowClick");
        }
        const { recd, renm, useyn } = e.data;
        if (!recd) return;
        setCurrentRecd(recd);
        setCurrentRenm(renm || "");
        setCurrentUseyn(useyn || "");
        onSearchRight(recd);
    };

    const onLeftRowSelected = () => {
        const selected = grdRST02List.current?.getSelectedNodes?.() || [];
        setLeftSelectedCount(selected.length);
    };

    const onDetailRowSelected = () => {
        const selected = grdRST02Detail.current?.getSelectedNodes?.() || [];
        setDetailSelectedCount(selected.length);
    };

    // ─── 우측 신규: ResiGrp 팝업 열기 ─────────────────────────────────────
    const onAddRow2 = () => {
        if (!currentRecd) {
            setMessage({ openAlert: true, severity: "info", msg: "좌측에서 상주원을 먼저 선택하십시오." });
            return;
        }
        if (currentUseyn !== "Y") {
            setMessage({ openAlert: true, severity: "warning", msg: "미사용 상주원에는 품목그룹을 배정할 수 없습니다." });
            return;
        }
        setResiGrpOpen(true);
    };

    // ─── 우측 삭제 ─────────────────────────────────────────────────────────
    const onDelete2 = () => {
        const selected = grdRST02Detail.current?.getSelectedNodes() || [];
        if (selected.length === 0) {
            setMessage({ openAlert: true, severity: "info", msg: "선택한 데이터가 없습니다." });
            return;
        }
        setDlgMessage({ openDlg: true, dlgTrans: "D_RIGHT", dlgMsg: "선택한 품목그룹 배정을 삭제하시겠습니까?" });
    };

    // ─── AlertDialog 처리 ─────────────────────────────────────────────────
    const AlertDialogResult = (e) => {
        setDlgMessage({ openDlg: false, dlgTrans: "", dlgMsg: "" });
        if (e.result === "N") return;
        if (e.Trans === "D_LEFT") {
            setDeleteApi(SERVER_URL + "/rst02/resi/delete");
        }
        if (e.Trans === "D_RIGHT") {
            setDeleteApi2(SERVER_URL + "/rst02/resi/grpdelete");
        }
    };

    const onDeleteResidentRefresh = () => {
        setCurrentRecd("");
        setCurrentRenm("");
        setCurrentUseyn("");
        setClearGrid2(true);
        onSearchLeft();
    };

    // ─── ResiGrp 팝업 닫기 → 우측 재조회 ────────────────────────────────
    const onResiGrpClose = () => {
        setResiGrpOpen(false);
        if (currentRecd) onSearchRight(currentRecd);
    };

    // ─── 검색조건 ─────────────────────────────────────────────────────────
    const optionsX = {
        0: [
            // {
            //     formatter: "select", name: "kVnt1", labelName: "T1업체", type: "text",
            //     defaultValue: idata.kVnt1, codeMode: true, selectAll: "",
            //     codes: null, comms: idata.vnt1List, align: "left",
            // },
            {
                formatter: "input", name: "kRecd", labelName: "상주원코드", type: "text",
                defaultValue: idata.kRecd, readOnly: false, align: "left", labelType: "none",
            },
            {
                formatter: "select", name: "kUseyn", labelName: "사용여부", type: "text",
                defaultValue: idata.kUseyn, codeMode: false, selectAll: "",
                codes: useYnList, comms: null, align: "left",
            },
            { formatter: "blank", align: "right" },
            { formatter: "blank", align: "right" },
        ],
    };

    // ─── 레이아웃 토글 ────────────────────────────────────────────────────
    const onClick = () => {
        const next = !panded;
        setPanded(next);
        setStyle({
            height: next
                ? "calc(100vh - 65px - 35px - 50px - 21px - 56px - 8px)"
                : "calc(100vh - 65px - 35px - 50px - 21px - 8px)",
        });
    };

    // ─── 좌측 컬럼 정의 ───────────────────────────────────────────────────
    const columnDefs1 = [
        // {
        //     headerName: "업체코드", field: "vand",
        //     sortable: true, filter: true, flex: 1, minWidth: 90,
        //     editable: false, cellClass: ["nocheckbox", "alignC"],
        //     checkboxSelection: true, headerCheckboxSelection: true,
        // },
        {
            headerName: "상주원코드", field: "recd",
            sortable: true, filter: true, flex: 1, minWidth: 100,
            editable: false, headerClass: "", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true,
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "상주원명", field: "renm",
            sortable: true, filter: true, flex: 1, minWidth: 80,
            editable: true, /* headerClass: "editCellOnlySub",  */cellClass: ["nocheckbox", "alignL"],
            singleClickEdit: true,
            checkboxSelection: false, headerCheckboxSelection: false,
            cellEditorParams: { maxLength: RESIDENT_NAME_MAX_BYTES },
            valueParser: params => truncateByUtf8Bytes(params.newValue, RESIDENT_NAME_MAX_BYTES),
        },
        {
            headerName: "연락처", field: "reph",
            sortable: true, filter: true, flex: 1, minWidth: 120,
            editable: true, headerClass: "editCellOnlySub", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true,
            checkboxSelection: false, headerCheckboxSelection: false,
            cellEditor: "phoneEditor",
            cellEditorParams: { maxLength: 13 },
            valueFormatter: params => formatPhone(params.value),
        },
        {
            headerName: "사용여부", field: "useyn",
            sortable: true, filter: true, flex: 1, minWidth: 80,
            editable: true, headerClass: "editCellOnlySub", cellClass: ["nocheckbox", "alignC"],
            singleClickEdit: true,
            checkboxSelection: false, headerCheckboxSelection: false,
            cellEditor: "agSelectCellEditor",
            cellEditorParams: { values: ["Y", "N"] },
            valueFormatter: params => params.value === "Y" ? "사용" : params.value === "N" ? "미사용" : params.value ?? "",
        },
    ];

    // ─── 우측 컬럼 정의 (WRTRSGP 상주원-품목그룹 배정 목록) ─────────────
    const columnDefs2 = [
        {
            headerName: "품목그룹코드", field: "grpt",
            sortable: true, filter: true, flex: 1, minWidth: 110,
            editable: false, cellClass: ["nocheckbox", "alignL"],
            checkboxSelection: true, headerCheckboxSelection: true,
        },
        {
            headerName: "품목그룹명", field: "grnm",
            sortable: true, filter: true, flex: 2, minWidth: 130,
            editable: false, cellClass: ["nocheckbox", "alignL"],
            valueGetter: params => params.data?.grnm ?? params.data?.grptnm ?? "",
        },
    ];

    // ─── Render ───────────────────────────────────────────────────────────
    return (
        <>
            {/* Dialog */}
            <AlertDialog open={openDlg} trans={dlgTrans} message={dlgMsg} onClose={AlertDialogResult} />

            {/* Alert */}
            <CustomAlert
                open={openAlert} severity={severity} duration={4000} message={msg}
                onClose={() => setMessage({ openAlert: false, severity: "", msg: "" })}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            />

            {/* ResiGrp 팝업: 품목그룹코드 배정 */}
            <Popup
                type={"ResiGrp"}
                open={resiGrpOpen}
                wrcd={currentRecd}          // recd로 전달 (Popup에서 wrcd prop으로 ResiGrp.recd에 매핑)
                vnt1={idata.kVnt1}
                para={onResiGrpClose}       // onRefresh 콜백 (팝업 확인 후 우측 재조회)
                onClose={onResiGrpClose}
            />

            {/* Ribbon */}
            <Ribbon onSearch={onSearch} onSearchName={null}>
                {"완성차 재고관리 > 기준정보 > "}
                <b style={{ color: "#cd5c5c", fontFamily: "PretendardMedium, NanumGothic, sans-serif" }}>상주원등록</b>
            </Ribbon>

            {/* 검색조건 */}
            <div onKeyDown={onSearchKeyDown}><ContentXearch optionsX={optionsX} onChange={onChange} onClick={onClick} /></div>

            {/* Content */}
            <Content style={style}>
                <div className="grid_type02">

                    {/* ── 좌측: 상주원 마스터 ── */}
                    <div className="grid_wrap02" style={{ width: "50%", height: "100%" }}>
                        <div className="grid_area">
                            <GridRibbon title={"상주원 등록"}
                                onSearch={onSearch} onSearchName={"조회"}
                                onAddRow={onAddRow} onAddRowName={"신규"}
                                onInsert={onInsert} onInsertName={"입력"}
                                onInsertDisabled={leftSelectedCount === 0}
                                onUpdate={onUpdate} onUpdateName={"수정"}
                                onUpdateDisabled={leftSelectedCount === 0}
                                onDelete={onDelete} onDeleteName={"삭제"}
                                onDeleteDisabled={leftSelectedCount === 0}
                            />
                            <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                                <Grid
                                    ref={grdRST02List}
                                    gridId={"RST021"}
                                    columnDefs={columnDefs1}
                                    pagination={false}
                                    paginationCsv={true}
                                    paginationPageSize={"1000"}
                                    rowMultiSelectWithClick={true}
                                    suppressRowClickSelection={false}
                                    rowSelection={"multiple"}
                                    enableRangeSelection={false}
                                    enableCellTextSelection={true}
                                    isFirst={true}
                                    displayNo={true}
                                    searchApi={searchApi}
                                    insertApi={insertApi}
                                    updateApi={updateApi}
                                    transformInsertData={row => ({
                                        ...row,
                                        reph: String(row.reph ?? "").replace(/-/g, ""),
                                    })}
                                    insertRefresh={onSearchLeft}
                                    updateRefresh={onSearchLeft}
                                    deleteApi={deleteApi}
                                    deleteRefresh={onDeleteResidentRefresh}
                                    onClearApi={onClearApi}
                                    onRowClickEvent={onRowClicked}
                                    onRowSelectedEvent={onLeftRowSelected}
                                    onCellValueChangedEvent={e => {
                                        if (e?.node && !e.node.isSelected()) e.node.setSelected(true, false, "cellEdit");
                                    }}
                                    useMessage={false}
                                />
                            </div>
                        </div>
                    </div>

                    {/* ── 우측: 품목그룹 배정 목록 ── */}
                    <div className="grid_wrap03" style={{ height: "100%" }}>
                        <div className="grid_area">
                            <GridRibbon
                                title={currentRecd ? `품목그룹 배정 [${currentRecd}] ${currentRenm}${currentUseyn === "N" ? " (미사용)" : ""}` : "품목그룹 배정"}
                                onDelete={onDelete2} onDeleteName={"삭제"}
                                onDeleteDisabled={!currentRecd || detailSelectedCount === 0}
                            >
                                <button
                                    type="button"
                                    disabled={!currentRecd}
                                    onClick={onAddRow2}
                                    style={{
                                        display: "flex",
                                        flexFlow: "row nowrap",
                                        justifyContent: "flex-start",
                                        alignItems: "center",
                                        height: "30px",
                                        border: "1px solid #B7BCD6",
                                        borderRadius: "2px",
                                        background: currentRecd ? "#fff" : "#F1F2F5",
                                        color: currentRecd ? "#222" : "#9A9A9A",
                                        marginLeft: "10px",
                                        fontWeight: 600,
                                        cursor: currentRecd ? "pointer" : "not-allowed",
                                        paddingRight: "10px",
                                        paddingLeft: "10px",
                                        fontSize: "13px",
                                        fontFamily: "PretendardLight, NanumGothic, sans-serif",
                                    }}
                                >
                                    + 품목그룹 추가
                                </button>
                            </GridRibbon>
                            <div className="grid_cont_box" style={{ height: "calc(100%)" }}>
                                <Grid
                                    ref={grdRST02Detail}
                                    gridId={"RST022"}
                                    columnDefs={columnDefs2}
                                    pagination={false}
                                    paginationCsv={true}
                                    paginationPageSize={"1000"}
                                    rowMultiSelectWithClick={true}
                                    suppressRowClickSelection={false}
                                    rowSelection={"multiple"}
                                    enableRangeSelection={false}
                                    enableCellTextSelection={true}
                                    isFirst={false}
                                    displayNo={true}
                                    searchApi={searchApi2}
                                    deleteApi={deleteApi2}
                                    deleteRefresh={() => currentRecd && onSearchRight(currentRecd)}
                                    onClearApi={onClearApi2}
                                    onRowSelectedEvent={onDetailRowSelected}
                                    onCellValueChangedEvent={async () => { }}
                                    useMessage={false}
                                    clearGrid={clearGrid2}
                                    onClearGridFlag={setClearGrid2}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </Content>
        </>
    );
}

export default RST02;
